import { FC } from 'react';
import { Button, Container, Group, Paper, Stack, Text } from '@mantine/core';
import { DiagnosticReportTimeline } from '../patient/diagnostic-report-timeline/DiagnosticReportTimeline';
import { Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { HL7 } from '@consts/systems.consts';
import { createReference, normalizeErrorString } from '@medplum/core';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck, IconCircleOff } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

const THREE_MONTHS_MS = 3 * 30 * 24 * 60 * 60 * 1000;

// -----------------------------------------------------------------------------
interface RiskAssessmentReviewTaskProps {
  patientId: string;
  task: Task;
}

// -----------------------------------------------------------------------------
export const RiskAssessmentReviewTask: FC<RiskAssessmentReviewTaskProps> = ({
  patientId,
  task,
}) => {
  const medplum = useMedplum();
  const { t } = useTranslation(['common', 'tasks']);

  // ---------------------------------------------------------------------------
  const riskAssessmentReference = task.focus?.reference;

  // ---------------------------------------------------------------------------
  const onClickSchedule = async (): Promise<void> => {
    try {
      const patient = await medplum.readResource('Patient', patientId);
      const newTask: Task = {
        intent: 'order',
        resourceType: 'Task',
        status: 'requested',
        priority: 'routine',
        code: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.RISK_ASSESSMENT.REVIEW.BASE,
              code: HL7.TERMINOLOGY.CS.RISK_ASSESSMENT.REVIEW.CODES.REMINDER,
              display: 'Υπενθύμιση Επανεκτίμησης',
            },
          ],
        },
        description: t('riskAssessmentReview.followUpDescription', {
          ns: 'tasks',
        }),
        focus: {
          reference: riskAssessmentReference,
        },
        for: createReference(patient),
        authoredOn: new Date().toISOString(),
        executionPeriod: {
          start: new Date(Date.now() + THREE_MONTHS_MS).toISOString(),
        },
      };

      await medplum.createResource(newTask);
      await medplum.updateResource({ ...task, status: 'completed' });
      notifications.show({
        color: 'green',
        icon: <IconCircleCheck />,
        title: t('success', { ns: 'common' }),
        message: `${t(
          'riskAssessmentReview.notifications.successfullyScheduled',
          {
            ns: 'tasks',
          }
        )}.`,
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        icon: <IconCircleOff />,
        title: t('error', { ns: 'common' }),
        message: normalizeErrorString(error),
      });
    }
  };

  // ---------------------------------------------------------------------------
  const onClickDismiss = async (): Promise<void> => {
    try {
      await medplum.updateResource({
        ...task,
        status: 'completed',
      });
      notifications.show({
        color: 'green',
        icon: <IconCircleCheck />,
        title: t('success', { ns: 'common' }),
        message: `${t(
          'riskAssessmentReview.notifications.successfullyDismissed',
          {
            ns: 'tasks',
          }
        )}.`,
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        icon: <IconCircleOff />,
        title: t('error', { ns: 'common' }),
        message: normalizeErrorString(error),
      });
    }
  };

  // ---------------------------------------------------------------------------
  return (
    <Container size="none">
      <Group mt="md" align="flex-start">
        <Container flex={1} maw={1200} p="md">
          <DiagnosticReportTimeline
            patientId={patientId}
            riskAssessmentReference={riskAssessmentReference}
            diagnosticReportLimit={3}
            flexDirection="column-reverse"
          />
        </Container>

        <Container p="xl" mt="md">
          <Stack>
            {/* task status indicator */}
            <Paper p="md">
              <Text component="h2" size="lg" fw={700}>
                {t('riskAssessmentReview.title', {
                  ns: 'tasks',
                })}
              </Text>
              <Text component="p" size="md" fw={500}>
                {`${t('riskAssessmentReview.status', {
                  ns: 'tasks',
                })}: ${task.status}`}
              </Text>
              <Text component="p" size="md" fw={500}>
                {`${t('riskAssessmentReview.priority', {
                  ns: 'tasks',
                })}: ${task.priority}`}
              </Text>
              <Text component="p" size="md" fw={500}>
                {`${t('riskAssessmentReview.created', {
                  ns: 'tasks',
                })}: ${new Date(task.authoredOn || '').toLocaleDateString()}`}
              </Text>
              {task.executionPeriod && (
                <Text component="p" size="md" fw={500}>
                  {`${t('riskAssessmentReview.period', {
                    ns: 'tasks',
                  })}: ${new Date(
                    task.executionPeriod.start || ''
                  ).toLocaleDateString()}`}
                </Text>
              )}
            </Paper>

            <Paper p="md">
              <Stack>
                {task.code?.coding?.find(
                  (coding) =>
                    coding.system ===
                      HL7.TERMINOLOGY.CS.RISK_ASSESSMENT.REVIEW.BASE &&
                    coding.code ===
                      HL7.TERMINOLOGY.CS.RISK_ASSESSMENT.REVIEW.CODES.REVIEW
                ) && (
                  <Button onClick={onClickSchedule}>
                    {`${t('riskAssessmentReview.scheduleReview', {
                      ns: 'tasks',
                    })}`}
                  </Button>
                )}
                <Button onClick={onClickDismiss}>
                  {' '}
                  {`${t('riskAssessmentReview.dismiss', {
                    ns: 'tasks',
                  })}`}
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Group>
    </Container>
  );
};
